import mixpanel from "mixpanel-browser"

// GDPR-first Mixpanel wrapper. Mixpanel is initialised with tracking OPTED OUT.
// Nothing is sent until the visitor grants the `analytics` cookie category, at
// which point `enableTracking()` opts in. Revoking consent opts back out and
// clears the distinct_id. No-ops entirely when the project token is absent
// (dev/test/unconfigured), keeping those environments silent.

const meta = (name) => document.querySelector(`meta[name="${name}"]`)?.content
const token = () => meta("mixpanel-token")
// Must match the project's data residency. This project is EU; the server sets
// a `mixpanel-api-host` meta tag. Falling back to the US host would make events
// vanish (accepted upstream but never ingested into an EU project).
const apiHost = () => meta("mixpanel-api-host") || "api-eu.mixpanel.com"

let initialized = false

export function initMixpanel() {
  if (initialized) return
  const projectToken = token()
  if (!projectToken) return // unconfigured environment — stay silent

  mixpanel.init(projectToken, {
    opt_out_tracking_by_default: true,
    persistence: "localStorage",
    ignore_dnt: false,
    api_host: `https://${apiHost()}`,
    // Session Replay. record_sessions_percent stays 0 so nothing auto-records
    // before consent — recording is started manually in enableTracking().
    // Replay routes to the same (EU) api_host. Defaults mask all text/inputs;
    // images/video are blocked. Keep these on for GDPR.
    record_sessions_percent: 0,
    record_mask_all_text: true,
    record_mask_all_inputs: true,
    record_block_selector: "img, video",
  })
  initialized = true
}

export function enableTracking() {
  if (!initialized) return
  mixpanel.opt_in_tracking()
  // Screen recording is privacy-sensitive — only after analytics consent.
  mixpanel.start_session_recording?.()
}

export function disableTracking() {
  if (!initialized) return
  mixpanel.stop_session_recording?.()
  mixpanel.opt_out_tracking()
  mixpanel.reset()
}

export function track(event, properties = {}) {
  if (!initialized || mixpanel.has_opted_out_tracking()) return
  mixpanel.track(event, properties)
}

// Use the database primary key as $user_id — never email. Call on login and on
// every authenticated page load.
export function identify(userId, traits = {}) {
  if (!initialized || mixpanel.has_opted_out_tracking()) return
  mixpanel.identify(String(userId))
  if (Object.keys(traits).length) mixpanel.people.set(traits)
}

// Call on logout, without exception.
export function reset() {
  if (!initialized) return
  mixpanel.reset()
}
